import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Una fila de la tabla resumen (label + valor ya formateado).
export interface FilaPdf {
  label: string;
  valor: string;
  destacado?: boolean;
}

// Una fila de la tabla de gateways, con los nombres ya resueltos.
export interface GatewayPdf {
  gw: string;
  nombre?: string;
  cupo: number;
  cupoMin: number;
  cupoMax: number;
  ocupacion: number;
  tasaFalloPct: number;
  ackOk: number;
  ackFalse: number;
  ackFalsePct: number;
  enEspera: number;
  diferidos: number;
  fugas: number;
}

export interface SeriePdf {
  nombre: string;
  color: string;
  datos: number[];
}

// Un gráfico del informe. Las barras se agrupan por categoría (una barra por
// serie); las líneas comparten el eje X de las franjas.
export interface GraficoPdf {
  titulo: string;
  tipo: 'barras' | 'lineas';
  categorias: string[];
  series: SeriePdf[];
  unidad?: string;
}

export interface MatrizPdf {
  // Valor por [fila][columna] — filas = gateways, columnas = franjas.
  celdas: number[][];
  filas: string[]; // nombres de gateway (texto plano, sin HTML)
  columnas: string[]; // etiquetas de franja
  unidad?: string; // '%' para la tasa de fallo
}

export interface InformeDownlinksPdf {
  periodo: string;
  cliente?: string;
  resumen: FilaPdf[];
  gateways: GatewayPdf[];
  cupo?: MatrizPdf;
  tasaFallo?: MatrizPdf;
  barras: GraficoPdf[];
  series: GraficoPdf[];
  // Config del controlador de congestión, si el backend la expone.
  congestion?: Record<string, string>;
}

// Paleta del informe. Gris azulado para la estructura y un azul sobrio para los
// acentos: es un documento técnico, no un tablero.
const TINTA = { r: 30, g: 41, b: 59 }; // slate 800
const TINTA_SUAVE = { r: 100, g: 116, b: 139 }; // slate 500
const LINEA = { r: 203, g: 213, b: 225 }; // slate 300
const ACENTO = { r: 37, g: 99, b: 235 }; // blue 600

const MARGEN = 14; // mm
const ANCHO_LOGO = 22; // mm
const ALTO_ENCABEZADO = 39; // mm hasta la regla de cierre del encabezado
const ALTO_PIE = 16; // mm reservados abajo

/**
 * Informe PDF del dashboard de métricas de downlinks.
 *
 * Los gráficos se dibujan a mano en un canvas y se insertan como imagen, en vez
 * de exportar los de Highcharts: las etiquetas del eje Y de los heatmaps del
 * dashboard son HTML (nombre del gateway + EUI en dos líneas), y esas etiquetas
 * viven FUERA del SVG, así que al rasterizar el SVG exportado se perderían justo
 * los nombres. Dibujarlos acá además deja el informe con la misma escala de
 * color siempre, sin depender del tema de la pantalla.
 */
@Injectable({ providedIn: 'root' })
export class MetricasDownlinksPdfService {
  /** Arma el informe y lo descarga. */
  async generar(info: InformeDownlinksPdf): Promise<void> {
    const doc = await this.construir(info);
    doc.save(this.nombreArchivo(info));
  }

  /**
   * Arma el informe y devuelve el documento sin guardarlo. Existe aparte de
   * `generar` para poder previsualizar el layout sin disparar una descarga.
   */
  async construir(info: InformeDownlinksPdf): Promise<jsPDF> {
    const doc = new jsPDF({
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
    });
    const logo = await this.logo().catch(
      (): { dataUrl: string; w: number; h: number } | undefined => undefined,
    );
    const ancho = doc.internal.pageSize.getWidth();
    const anchoUtil = ancho - MARGEN * 2;

    let y = this.encabezado(doc, info, logo);

    // ── 1. Resumen del período ──
    y = this.tituloSeccion(doc, '1. Resumen del período', y + 2);
    autoTable(doc, {
      startY: y,
      margin: {
        left: MARGEN,
        right: MARGEN,
        top: ALTO_ENCABEZADO + 6,
        bottom: ALTO_PIE,
      },
      head: [['Métrica', 'Valor']],
      body: info.resumen.map((f) => [f.label, f.valor || 'Sin información']),
      styles: { fontSize: 8.5, cellPadding: 1.8, textColor: [30, 41, 59] },
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: 255,
        fontSize: 8,
        halign: 'left',
      },
      columnStyles: {
        0: { cellWidth: anchoUtil * 0.68 },
        1: { halign: 'right', fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      // Las filas clave del resumen van resaltadas, igual que en el dashboard.
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (info.resumen[data.row.index]?.destacado) {
          data.cell.styles.fillColor = [237, 233, 254];
          data.cell.styles.fontStyle = 'bold';
        }
      },
      // Al saltar de página, autoTable redibuja el encabezado del informe.
      didDrawPage: () => {
        this.encabezado(doc, info, logo);
      },
    });
    y = this.finTabla(doc);

    // ── 2. Cupos por gateway ──
    if (info.gateways.length) {
      // Sin esto, el título y el encabezado de la tabla quedan al pie de una
      // página y las filas arrancan en la siguiente.
      y = this.espacio(doc, info, logo, y + 6, 34);
      y = this.tituloSeccion(doc, '2. Cupos por gateway', y);
      autoTable(doc, {
        startY: y,
        margin: {
          left: MARGEN,
          right: MARGEN,
          top: ALTO_ENCABEZADO + 6,
          bottom: ALTO_PIE,
        },
        head: [
          [
            'Gateway',
            'Cupo',
            'Rango',
            'Ocupación',
            'Tasa de fallo',
            'ACK false',
            'Esperando cupo',
            'Fugas',
          ],
        ],
        body: info.gateways.map((g) => [
          g.nombre ? `${g.nombre}\n${g.gw}` : g.gw,
          `${g.cupo}`,
          `${g.cupoMin} – ${g.cupoMax}`,
          `${g.ocupacion} / ${g.cupo}`,
          `${g.tasaFalloPct} %`,
          `${g.ackFalsePct} %\n${g.ackFalse} / ${g.ackOk + g.ackFalse}`,
          `${g.enEspera} ahora\n${g.diferidos} veces`,
          `${g.fugas}`,
        ]),
        styles: {
          fontSize: 7.5,
          cellPadding: 1.6,
          textColor: [30, 41, 59],
          valign: 'middle',
        },
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontSize: 7 },
        columnStyles: {
          0: { cellWidth: 44 },
          1: { halign: 'right' },
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' },
          7: { halign: 'right' },
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didDrawPage: () => {
          this.encabezado(doc, info, logo);
        },
      });
      y = this.finTabla(doc);
    }

    // ── 3. Evolución por franja y gateway (heatmaps) ──
    const heatmaps: [string, MatrizPdf | undefined][] = [
      ['Cupo de planes simultáneos', info.cupo],
      ['Tasa de fallo del gateway (%)', info.tasaFallo],
    ];
    if (heatmaps.some(([, m]) => m?.filas.length)) {
      y = this.espacio(doc, info, logo, y + 6, 30);
      y = this.tituloSeccion(doc, '3. Evolución por franja y gateway', y);
      for (const [titulo, matriz] of heatmaps) {
        if (!matriz?.filas.length) continue;
        const { png, aspecto } = this.heatmapPng(matriz);
        const alto = anchoUtil * aspecto;
        y = this.espacio(doc, info, logo, y, alto + 8);
        y = this.subtitulo(doc, titulo, y);
        doc.addImage(png, 'PNG', MARGEN, y, anchoUtil, alto);
        y += alto + 8;
      }
    }

    // ── 4. Distribuciones (barras) ──
    const barras = info.barras.filter((g) =>
      g.series.some((s) => s.datos.some((v) => v > 0)),
    );
    if (barras.length) {
      y = this.espacio(doc, info, logo, y + 4, 30);
      y = this.tituloSeccion(doc, '4. Distribuciones', y);
      for (const g of barras) {
        const { png, aspecto } = this.barrasPng(g);
        const alto = anchoUtil * aspecto;
        y = this.espacio(doc, info, logo, y, alto + 14);
        y = this.subtitulo(doc, g.titulo, y);
        doc.addImage(png, 'PNG', MARGEN, y, anchoUtil, alto);
        y += alto + 4;
        if (g.series.length > 1) y = this.leyenda(doc, g.series, y + 2) + 5;
        else y += 4;
      }
    }

    // ── 5. Series temporales ──
    const series = info.series.filter(
      (g) =>
        g.categorias.length && g.series.some((s) => s.datos.some((v) => v > 0)),
    );
    if (series.length) {
      y = this.espacio(doc, info, logo, y + 4, 30);
      y = this.tituloSeccion(doc, '5. Series temporales', y);
      for (const g of series) {
        const { png, aspecto } = this.lineasPng(g);
        const alto = anchoUtil * aspecto;
        y = this.espacio(doc, info, logo, y, alto + 14);
        y = this.subtitulo(doc, g.titulo, y);
        doc.addImage(png, 'PNG', MARGEN, y, anchoUtil, alto);
        y += alto + 4;
        y = this.leyenda(doc, g.series, y + 2) + 5;
      }
    }

    // ── 6. Configuración del controlador ──
    if (info.congestion && Object.keys(info.congestion).length) {
      y = this.espacio(doc, info, logo, y + 2, 34);
      y = this.tituloSeccion(
        doc,
        '6. Configuración del control de congestión',
        y,
      );
      autoTable(doc, {
        startY: y,
        margin: {
          left: MARGEN,
          right: MARGEN,
          top: ALTO_ENCABEZADO + 6,
          bottom: ALTO_PIE,
        },
        body: Object.entries(info.congestion).map(([k, v]) => [k, v]),
        styles: { fontSize: 8, cellPadding: 1.6, textColor: [30, 41, 59] },
        columnStyles: {
          0: { cellWidth: anchoUtil * 0.6 },
          1: { halign: 'right', fontStyle: 'bold' },
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        theme: 'plain',
        didDrawPage: () => {
          this.encabezado(doc, info, logo);
        },
      });
    }

    this.pies(doc);
    return doc;
  }

  // ══════════════════════ estructura del documento ══════════════════════

  /**
   * Encabezado del informe: logo, título y una banda de metadatos con etiquetas
   * (período, cliente, generación). La banda existe porque período y cliente
   * como dos renglones grises sueltos debajo del título se leían como una nota
   * al pie, no como los datos que identifican el informe.
   */
  private encabezado(
    doc: jsPDF,
    info: InformeDownlinksPdf,
    logo?: { dataUrl: string; w: number; h: number },
  ): number {
    const ancho = doc.internal.pageSize.getWidth();
    const yLogo = 10;
    let finLogo = yLogo;
    if (logo) {
      // Alto derivado de las dimensiones REALES del archivo: con una relación
      // de aspecto hardcodeada el logo sale estirado o achatado.
      const altoLogo = (ANCHO_LOGO * logo.h) / logo.w;
      doc.addImage(logo.dataUrl, 'PNG', MARGEN, yLogo, ANCHO_LOGO, altoLogo);
      finLogo = yLogo + altoLogo;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    this.tinta(doc, TINTA);
    doc.text('Informe de métricas de downlinks', ancho - MARGEN, 18, {
      align: 'right',
    });

    // La regla va DEBAJO del logo, no a una altura fija: el logo mide lo que
    // mide el archivo y con una Y hardcodeada le cruzaba la cola por encima.
    const yRegla = Math.max(24, finLogo + 1.5);
    doc.setDrawColor(LINEA.r, LINEA.g, LINEA.b);
    doc.setLineWidth(0.3);
    doc.line(MARGEN, yRegla, ancho - MARGEN, yRegla);

    // Banda de metadatos en tres columnas. Las flechas no existen en la
    // codificación de las fuentes estándar de jsPDF (salen como un glifo raro y
    // descolocan el renglón entero), así que se reemplazan por un guion.
    const periodo = info.periodo.replace(/[→⟶>]/g, '–');
    const celdas: [string, string][] = [
      ['PERÍODO', periodo || 'Sin información'],
      ['CLIENTE', info.cliente || 'Sin información'],
      ['GENERADO', this.fechaHora(new Date())],
    ];
    const anchoCelda = (ancho - MARGEN * 2) / celdas.length;
    celdas.forEach(([etiqueta, valor], i) => {
      const x = MARGEN + anchoCelda * i;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      this.tinta(doc, TINTA_SUAVE);
      doc.text(etiqueta, x, yRegla + 5.5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      this.tinta(doc, TINTA);
      // El valor se parte en dos renglones si no entra en su columna.
      const lineas = doc.splitTextToSize(valor, anchoCelda - 4) as string[];
      doc.text(lineas.slice(0, 2), x, yRegla + 10);
    });

    doc.setDrawColor(LINEA.r, LINEA.g, LINEA.b);
    doc.setLineWidth(0.4);
    doc.line(MARGEN, ALTO_ENCABEZADO, ancho - MARGEN, ALTO_ENCABEZADO);
    doc.setFont('helvetica', 'normal');
    return ALTO_ENCABEZADO + 6;
  }

  private tituloSeccion(doc: jsPDF, texto: string, y: number): number {
    const ancho = doc.internal.pageSize.getWidth();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    this.tinta(doc, ACENTO);
    doc.text(texto, MARGEN, y);
    doc.setDrawColor(ACENTO.r, ACENTO.g, ACENTO.b);
    doc.setLineWidth(0.4);
    doc.line(MARGEN, y + 1.5, ancho - MARGEN, y + 1.5);
    doc.setFont('helvetica', 'normal');
    this.tinta(doc, TINTA);
    return y + 6;
  }

  private subtitulo(doc: jsPDF, texto: string, y: number): number {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    this.tinta(doc, TINTA);
    doc.text(texto, MARGEN, y);
    doc.setFont('helvetica', 'normal');
    return y + 3;
  }

  // Y donde terminó la última autoTable.
  private finTabla(doc: jsPDF): number {
    const d = doc as unknown as { lastAutoTable?: { finalY: number } };
    return (d.lastAutoTable?.finalY ?? 40) + 2;
  }

  // Asegura `alto` mm libres; si no entran, abre página y repite el encabezado.
  private espacio(
    doc: jsPDF,
    info: InformeDownlinksPdf,
    logo: { dataUrl: string; w: number; h: number } | undefined,
    y: number,
    alto: number,
  ): number {
    const limite = doc.internal.pageSize.getHeight() - ALTO_PIE;
    if (y + alto <= limite) return y;
    doc.addPage();
    return this.encabezado(doc, info, logo);
  }

  private leyenda(doc: jsPDF, series: SeriePdf[], y: number): number {
    const ancho = doc.internal.pageSize.getWidth();
    let x = MARGEN;
    let fila = y;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    for (const s of series) {
      const w = doc.getTextWidth(s.nombre) + 9;
      // Si no entra en la línea, sigue abajo.
      if (x + w > ancho - MARGEN) {
        x = MARGEN;
        fila += 4;
      }
      const [r, g, b] = this.rgb(s.color);
      doc.setFillColor(r, g, b);
      doc.rect(x, fila - 1.8, 2.4, 2.4, 'F');
      this.tinta(doc, TINTA_SUAVE);
      doc.text(s.nombre, x + 3.4, fila);
      x += w;
    }
    return fila;
  }

  private pies(doc: jsPDF): void {
    const paginas = doc.getNumberOfPages();
    const ancho = doc.internal.pageSize.getWidth();
    const alto = doc.internal.pageSize.getHeight();
    for (let i = 1; i <= paginas; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      this.tinta(doc, TINTA_SUAVE);
      doc.setDrawColor(LINEA.r, LINEA.g, LINEA.b);
      doc.setLineWidth(0.2);
      doc.line(MARGEN, alto - 11, ancho - MARGEN, alto - 11);
      doc.text('Informe de métricas de downlinks — Irix', MARGEN, alto - 7);
      doc.text(`Página ${i} de ${paginas}`, ancho - MARGEN, alto - 7, {
        align: 'right',
      });
    }
  }

  // Fecha explícita dd/mm/aaaa hh:mm. `toLocaleString` mete segundos, comas y
  // "a. m." según el navegador, y en un informe queda sucio.
  private fechaHora(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return (
      `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ` +
      `${p(d.getHours())}:${p(d.getMinutes())}`
    );
  }

  private nombreArchivo(info: InformeDownlinksPdf): string {
    const f = new Date().toISOString().slice(0, 10);
    const cliente = (info.cliente ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return `metricas-downlinks${cliente ? `-${cliente}` : ''}-${f}.pdf`;
  }

  // ══════════════════════ gráficos ══════════════════════
  // Todos dibujan en un canvas a 2× y devuelven la relación de aspecto, para que
  // el PDF los inserte con el ancho de la caja de texto sin deformarlos.

  private lienzo(
    w: number,
    h: number,
  ): [HTMLCanvasElement, CanvasRenderingContext2D | null] {
    const c = document.createElement('canvas');
    c.width = w * 2;
    c.height = h * 2;
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.scale(2, 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.textBaseline = 'middle';
    }
    return [c, ctx];
  }

  // Grilla horizontal + eje Y con 4 divisiones. Devuelve la proyección vertical
  // para que la use el gráfico.
  private ejes(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    max: number,
    izq: number,
    abajo: number,
    unidad?: string,
  ): (v: number) => number {
    const arriba = 16;
    // Divisiones REDONDAS. Con `max/4` a secas el eje sale con 49,5 / 148,5 /
    // 428,3: números que nadie usa para leer un gráfico.
    const paso = this.pasoLindo(max / 4);
    const tope = Math.max(paso * 4, Math.ceil(max / paso) * paso);
    const py = (v: number) => h - abajo - (v / tope) * (h - abajo - arriba);
    ctx.font = '11px Helvetica, Arial, sans-serif';
    ctx.textAlign = 'right';
    for (let v = 0; v <= tope + 0.0001; v += paso) {
      const y = py(v);
      const i = Math.round(v / paso);
      ctx.strokeStyle = i === 0 ? '#cbd5e1' : '#eef2f7';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(izq, y);
      ctx.lineTo(w - 12, y);
      ctx.stroke();
      ctx.fillStyle = '#64748b';
      ctx.fillText(`${this.fmtCorto(v)}${unidad ?? ''}`, izq - 6, y);
    }
    return py;
  }

  /**
   * Etiquetas del eje X. Si entran derechas van derechas; si no, se saltean y se
   * rotan. Antes se rotaban siempre y cada N fijo, y con pocas franjas quedaban
   * tres etiquetas torcidas en un gráfico vacío.
   */
  private etiquetasX(
    ctx: CanvasRenderingContext2D,
    cats: string[],
    px: (i: number) => number,
    yBase: number,
    anchoDisponible: number,
  ): void {
    if (!cats.length) return;
    ctx.fillStyle = '#64748b';
    ctx.font = '10px Helvetica, Arial, sans-serif';
    const anchoMax = Math.max(...cats.map((c) => ctx.measureText(c).width), 1);
    const derechas = anchoMax * cats.length * 1.2 <= anchoDisponible;
    const paso = derechas
      ? 1
      : Math.max(
          1,
          Math.ceil((anchoMax * cats.length * 0.85) / anchoDisponible),
        );
    for (let i = 0; i < cats.length; i += paso) {
      const x = px(i);
      if (derechas) {
        ctx.textAlign = 'center';
        ctx.fillText(cats[i] ?? '', x, yBase + 10);
        continue;
      }
      ctx.save();
      ctx.translate(x, yBase + 8);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.fillText(cats[i] ?? '', 0, 0);
      ctx.restore();
    }
  }

  // Barras agrupadas: una barra por serie dentro de cada categoría.
  private barrasPng(g: GraficoPdf): { png: string; aspecto: number } {
    const w = 1180;
    // 1180×265 → ~41 mm de alto en la página: entran cuatro gráficos por
    // página y no quedan medias páginas vacías entre secciones.
    const h = 265;
    const izq = 62;
    const abajo = 56;
    const [c, ctx] = this.lienzo(w, h);
    if (!ctx) return { png: '', aspecto: 1 };

    const max = Math.max(
      1,
      ...g.series.flatMap((s) =>
        s.datos.map((v) => (Number.isFinite(v) ? v : 0)),
      ),
    );
    const py = this.ejes(ctx, w, h, max, izq, abajo, g.unidad);

    const n = Math.max(1, g.categorias.length);
    const anchoGrupo = (w - izq - 12) / n;
    // Una sola serie puede ocupar casi todo el grupo; con varias hay que dejar
    // aire entre grupos. Antes el tope era 30 px sobre un lienzo de 1180 y las
    // barras salían como hilos.
    const topeBarra = g.series.length === 1 ? 90 : 42;
    const anchoBarra = Math.max(
      3,
      Math.min(topeBarra, (anchoGrupo * 0.78) / g.series.length),
    );
    const px = (i: number) => izq + anchoGrupo * i + anchoGrupo / 2;

    g.categorias.forEach((_, i) => {
      const centro = px(i);
      const anchoTotal = anchoBarra * g.series.length;
      g.series.forEach((s, j) => {
        const v = s.datos[i] ?? 0;
        const [r, gg, b] = this.rgb(s.color);
        const x = centro - anchoTotal / 2 + j * anchoBarra;
        const yTop = py(v);
        ctx.fillStyle = `rgb(${r},${gg},${b})`;
        ctx.fillRect(x, yTop, Math.max(1, anchoBarra - 1), py(0) - yTop);
        // Valor arriba de la barra: sólo si la barra es ancha y hay pocas.
        if (v > 0 && anchoBarra >= 13 && g.categorias.length <= 14) {
          ctx.fillStyle = '#475569';
          ctx.font = '10px Helvetica, Arial, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(this.fmtCorto(v), x + anchoBarra / 2, yTop - 7);
        }
      });
    });

    this.etiquetasX(ctx, g.categorias, px, py(0), w - izq - 12);
    return { png: c.toDataURL('image/png'), aspecto: h / w };
  }

  // Líneas con marcadores. El eje X son las franjas del período.
  private lineasPng(g: GraficoPdf): { png: string; aspecto: number } {
    const w = 1180;
    const h = 265;
    const izq = 62;
    const abajo = 56;
    const [c, ctx] = this.lienzo(w, h);
    if (!ctx) return { png: '', aspecto: 1 };

    const max = Math.max(
      1,
      ...g.series.flatMap((s) =>
        s.datos.map((v) => (Number.isFinite(v) ? v : 0)),
      ),
    );
    const py = this.ejes(ctx, w, h, max, izq, abajo, g.unidad);

    // Con una sola franja no hay línea que dibujar: se usa el centro y un punto.
    const n = Math.max(1, g.categorias.length - 1);
    const px = (i: number) =>
      g.categorias.length === 1
        ? (izq + w - 12) / 2
        : izq + (i * (w - izq - 12)) / n;

    for (const s of g.series) {
      const [r, gg, b] = this.rgb(s.color);
      ctx.strokeStyle = `rgb(${r},${gg},${b})`;
      ctx.fillStyle = `rgb(${r},${gg},${b})`;
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.beginPath();
      s.datos.forEach((v, i) => {
        const x = px(i);
        const y = py(v);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      // Marcadores: con muchas franjas se vuelven ruido.
      if (g.categorias.length <= 40) {
        s.datos.forEach((v, i) => {
          ctx.beginPath();
          ctx.arc(px(i), py(v), 2.4, 0, Math.PI * 2);
          ctx.fill();
        });
      }
    }

    this.etiquetasX(ctx, g.categorias, px, py(0), w - izq - 12);
    return { png: c.toDataURL('image/png'), aspecto: h / w };
  }

  // Heatmap franja × gateway. Escala secuencial (claro → azul oscuro): el valor
  // no tiene signo ni punto medio, así que una secuencial es lo correcto y se
  // lee igual impresa en gris.
  private heatmapPng(m: MatrizPdf): { png: string; aspecto: number } {
    const filas = m.filas.length;
    const cols = Math.max(1, m.columnas.length);
    const anchoEtiquetas = 190;
    // Alto del eje X + un renglón aparte para la escala de color: si comparten
    // renglón, la referencia se mete entre las etiquetas de las franjas.
    const altoEjeX = 74;
    const w = 1180;
    const celda = (w - anchoEtiquetas - 12) / cols;
    const altoCelda = 24;
    const h = filas * altoCelda + altoEjeX;
    const [c, ctx] = this.lienzo(w, h);
    if (!ctx) return { png: '', aspecto: 1 };

    const max = Math.max(
      1,
      ...m.celdas.flatMap((f) => f.map((v) => (Number.isFinite(v) ? v : 0))),
    );

    for (let y = 0; y < filas; y++) {
      ctx.fillStyle = '#334155';
      ctx.textAlign = 'right';
      ctx.font = '13px Helvetica, Arial, sans-serif';
      ctx.fillText(
        this.acortar(m.filas[y], 26),
        anchoEtiquetas - 8,
        y * altoCelda + altoCelda / 2,
      );
      for (let x = 0; x < cols; x++) {
        const v = m.celdas[y]?.[x] ?? 0;
        ctx.fillStyle = this.colorEscala(v / max);
        ctx.fillRect(
          anchoEtiquetas + x * celda,
          y * altoCelda + 1,
          Math.max(1, celda - 1),
          altoCelda - 2,
        );
        // El número entra sólo si la celda es lo bastante ancha.
        if (celda >= 26 && v > 0) {
          ctx.fillStyle = v / max > 0.55 ? '#ffffff' : '#1e293b';
          ctx.textAlign = 'center';
          ctx.font = '11px Helvetica, Arial, sans-serif';
          ctx.fillText(
            this.fmtCorto(v),
            anchoEtiquetas + x * celda + celda / 2,
            y * altoCelda + altoCelda / 2,
          );
        }
      }
    }

    this.etiquetasX(
      ctx,
      m.columnas,
      (i) => anchoEtiquetas + i * celda + celda / 2,
      filas * altoCelda,
      w - anchoEtiquetas - 12,
    );

    // Referencia de la escala, abajo a la derecha.
    const anchoEscala = 150;
    const xEscala = w - 12 - anchoEscala;
    const yEscala = h - 10;
    const grad = ctx.createLinearGradient(xEscala, 0, xEscala + anchoEscala, 0);
    grad.addColorStop(0, this.colorEscala(0));
    grad.addColorStop(0.5, this.colorEscala(0.5));
    grad.addColorStop(1, this.colorEscala(1));
    ctx.fillStyle = grad;
    ctx.fillRect(xEscala, yEscala - 3, anchoEscala, 6);
    ctx.font = '10px Helvetica, Arial, sans-serif';
    ctx.fillStyle = '#64748b';
    ctx.textAlign = 'right';
    ctx.fillText(`0${m.unidad ?? ''}`, xEscala - 6, yEscala);
    ctx.textAlign = 'left';
    ctx.fillText(
      `${this.fmtCorto(max)}${m.unidad ?? ''}`,
      xEscala + anchoEscala + 6,
      yEscala,
    );

    return { png: c.toDataURL('image/png'), aspecto: h / w };
  }

  // ══════════════════════ helpers ══════════════════════

  // Escala secuencial slate-100 → blue-800.
  private colorEscala(t: number): string {
    const x = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0));
    const desde = [241, 245, 249];
    const hasta = [30, 64, 175];
    const c = desde.map((d, i) => Math.round(d + (hasta[i] - d) * x));
    return `rgb(${c[0]},${c[1]},${c[2]})`;
  }

  private rgb(hex: string): [number, number, number] {
    const h = (hex || '#888888').replace('#', '');
    const n = parseInt(
      h.length === 3
        ? h
            .split('')
            .map((c) => c + c)
            .join('')
        : h,
      16,
    );
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  private tinta(doc: jsPDF, c: { r: number; g: number; b: number }): void {
    doc.setTextColor(c.r, c.g, c.b);
  }

  // Paso "lindo" para el eje: 1, 2, 2,5 o 5 por potencia de 10. Es lo que hace
  // que el eje diga 0 / 150 / 300 / 450 en vez de 0 / 142,8 / 285,5 / 428,3.
  private pasoLindo(bruto: number): number {
    if (!Number.isFinite(bruto) || bruto <= 0) return 1;
    const exp = Math.floor(Math.log10(bruto));
    const base = Math.pow(10, exp);
    const rel = bruto / base;
    const mult =
      rel <= 1 ? 1 : rel <= 2 ? 2 : rel <= 2.5 ? 2.5 : rel <= 5 ? 5 : 10;
    return mult * base;
  }

  private acortar(texto: string, max: number): string {
    return texto.length <= max ? texto : `${texto.slice(0, max - 1)}…`;
  }

  // Números cortos para ejes y celdas: 1,2k en vez de 1200.
  private fmtCorto(v: number): string {
    if (!Number.isFinite(v)) return '0';
    if (Math.abs(v) >= 1000) {
      const k = Math.round((v / 1000) * 10) / 10;
      return `${String(k).replace('.', ',')}k`;
    }
    const r = Math.round(v * 10) / 10;
    return Number.isInteger(r) ? String(r) : String(r).replace('.', ',');
  }

  // Logo de Irix como data URL + sus dimensiones reales (jsPDF no acepta una
  // ruta, y el alto se calcula con la relación real del archivo).
  private logo(): Promise<{ dataUrl: string; w: number; h: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext('2d');
        if (!ctx) {
          reject(new Error('No se pudo crear el canvas del logo'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve({
          dataUrl: c.toDataURL('image/png'),
          w: img.naturalWidth,
          h: img.naturalHeight,
        });
      };
      img.onerror = (e) => reject(e);
      img.src = 'assets/images/irix_logo.png';
    });
  }
}
